import React, {useEffect, useMemo, useState} from 'react';
import {getInventory, updateInventoryCount,} from "../../api/dataService";
import {useNavigate} from "react-router-dom";
import StackedContentContainer from "../ContentContainer";
import SearchableTable from "../SearchableTable";
import {Button, Col, Row as BRow} from "react-bootstrap";
import {useSelector} from "react-redux";
import {RootState} from "../../redux/reduxTypes";
import {isLoadingSpinner} from "../../util/Helpers";
import {DefaultCell, EditableCell} from "../EditableCell";
import {ColumnDef} from "@tanstack/react-table";
import {Inventory} from "../../types";
import Form from 'react-bootstrap/Form';


const parentStyle = {
    padding: '2rem',
};

interface DisplayInventory {
    [key: string]: any;  // Use 'any' for dynamic keys
    address: string;
}


const ManageEquipmentPage = () => {
    const [filterZeros, setFilterZeros] = useState(true);
    const [inventoryList, setInventoryList] = useState<DisplayInventory[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [footerData, setFooterData] = useState<{
        stockValues: { [key: string]: number },
        diff: { [key: string]: number },
        displayNames: { [key: string]: string }
    }>({
        stockValues: {},
        diff: {},
        displayNames: {}
    });
    const navigate = useNavigate();
    const user = useSelector((state: RootState) => state.user);


    useEffect(() => {
        getInventory().then(inventoryResponse => {
            if ('redirectTo' in inventoryResponse) {
                navigate(inventoryResponse.redirectTo);
            } else if (Array.isArray(inventoryResponse)) {
                const formattedList = transformInventoryData(inventoryResponse);
                setInventoryList(formattedList);
                const footerData = calculateDifferences(formattedList);
                setFooterData(footerData);
            }
            setIsLoading(false);
        }).catch(error => {
            console.error("Error fetching data:", error);
            setIsLoading(false);
        });
    }, []);

    const transformInventoryData = (inventoryData: Inventory[]): DisplayInventory[] => {
        const addressMap: { [key: string]: DisplayInventory } = {};

        inventoryData.forEach(({id, quantity, resource, address}) => {
            if (!resource) {
                console.error('Missing resource data:', resource);
                return; // Skip this item if it lacks essential resource data
            }

            // Only include resources of type 'equipment' or 'tool'
            if (!(resource.type === 'equipment' || resource.type === 'tool')) {
                return; // Skip resources that are not equipment or tools
            }

            const addressLabel = address.label || `${address.street}, ${address.city}, ${address.province}`;
            if (!addressMap[addressLabel]) {
                addressMap[addressLabel] = {
                    address: addressLabel
                };
            }
            // Create a unique key for each resource using both name and ID
            const resourceKey = `${resource.resource_name}_${resource.id}`;
            addressMap[addressLabel][resourceKey] = {
                quantity: parseFloat(quantity.toString()),
                resource,
                id,  // Store the inventory item's ID here for easy access during updates
                displayName: resource.resource_name // Store the display name separately
            };
        });

        // Sort addresses alphabetically
        return Object.values(addressMap).sort((a, b) => a.address.localeCompare(b.address));
    };

    const calculateDifferences = (inventory: DisplayInventory[]): {
        stockValues: { [key: string]: number },
        diff: { [key: string]: number },
        displayNames: { [key: string]: string }
    } => {
        const stockValues: { [key: string]: number } = {};
        const diff: { [key: string]: number } = {};
        const totalQuantities: { [key: string]: number } = {};
        const displayNames: { [key: string]: string } = {};

        // First pass: collect all stock values, display names, and initialize total quantities
        inventory.forEach(item => {
            Object.keys(item).forEach(key => {
                if (key !== 'address' && item[key] && item[key].resource) {
                    const stock = item[key].resource?.stock ?? 0;
                    const displayName = item[key].displayName || key;

                    if (stockValues[key] === undefined) {
                        stockValues[key] = stock;
                        totalQuantities[key] = 0;
                        displayNames[key] = displayName;
                    }
                }
            });
        });

        // Second pass: sum up all quantities for each resource
        inventory.forEach(item => {
            Object.keys(item).forEach(key => {
                if (key !== 'address' && item[key] && item[key].resource) {
                    const quantity = item[key].quantity ?? 0;
                    totalQuantities[key] += quantity;
                }
            });
        });

        // Calculate differences
        Object.keys(stockValues).forEach(key => {
            diff[key] = stockValues[key] - totalQuantities[key];
        });

        return {stockValues, diff, displayNames};
    };

    const updateInventory = async (addressLabel: string, resourceKey: string, newQuantity: number) => {
        // console.log(`Updating inventory: Address=${addressLabel}, ResourceKey=${resourceKey}, NewQuantity=${newQuantity}`);

        // Log all available resources for debugging
        // console.log("Available resources in inventory list:",
        //     inventoryList.map(item =>
        //         Object.keys(item).filter(key => key !== 'address')
        //     ).flat()
        // );

        // Find the item and resource to update
        let itemId;
        let foundItem = false;
        let foundResource = false;

        // Create a new list with the updated item
        const updatedList = inventoryList.map(item => {
            if (item.address === addressLabel) {
                foundItem = true;

                // Log all resources at this address for debugging
                // console.log(`Resources at address ${addressLabel}:`,
                //     Object.keys(item).filter(key => key !== 'address')
                // );

                const resourceInfo = item[resourceKey];
                // console.log(`Resource info for ${resourceKey}:`, resourceInfo);

                if (resourceInfo) {
                    foundResource = true;
                    itemId = resourceInfo.id;  // Capture the ID for backend update

                    // Create a new item with the updated resource
                    return {
                        ...item,
                        [resourceKey]: {
                            ...resourceInfo,
                            quantity: newQuantity
                        }
                    };
                } else {
                    console.warn(`Resource ${resourceKey} not found at address ${addressLabel}`);
                }
            }
            return item;
        });

        if (!foundItem) {
            console.warn(`Address ${addressLabel} not found in inventory list`);
        } else if (!foundResource) {
            console.warn(`Resource ${resourceKey} not found at address ${addressLabel}`);
        }

        // Update the inventory list state
        setInventoryList(updatedList);

        // Recalculate footer data with the updated list
        const newFooterData = calculateDifferences(updatedList);
        setFooterData(newFooterData);

        // Perform backend update using the captured ID
        if (itemId) {
            // console.log(`Updating backend with ID ${itemId} and quantity ${newQuantity}`);
            try {
                const result = await updateInventoryCount(itemId, {quantity: newQuantity});
                // console.log("Backend update result:", result);
            } catch (error) {
                console.error("Error updating inventory:", error);
            }
        } else {
            console.warn("No item ID found for backend update");
        }
    };

    const getFilteredInventory = useMemo(() => {
        if (!filterZeros) return inventoryList;
        return inventoryList.filter(item => {
            return Object.keys(item).some(key => key !== 'address' && item[key] && !(item[key].quantity === 0));
        });
    }, [filterZeros, inventoryList]);

    const toggleZeroFilter = () => {
        setFilterZeros(!filterZeros);
    };


    const columns = useMemo(() => {
        let cols: ColumnDef<DisplayInventory, any>[] = [
            {
                accessorKey: 'address',
                id: 'address',
                header: 'Address',
                enableColumnFilter: false,
                cell: ({getValue}) => <DefaultCell value={getValue()}/>
            },
        ];

        if (inventoryList.length > 0) {
            // Get resource keys and sort them alphabetically by display name
            const resourceKeys = Object.keys(inventoryList[0])
                .filter(key => key !== 'address')
                .sort((a, b) => {
                    const displayNameA = inventoryList[0][a]?.displayName || a;
                    const displayNameB = inventoryList[0][b]?.displayName || b;
                    return displayNameA.localeCompare(displayNameB);
                });

            resourceKeys.forEach(resourceKey => {
                // Get the display name from the first item that has this resource
                const displayName = inventoryList.find(item => item[resourceKey])
                    ?.[resourceKey]?.displayName || resourceKey;

                cols.push({
                    accessorKey: resourceKey,
                    id: resourceKey,
                    header: displayName, // Use the display name for the header
                    enableColumnFilter: false,
                    cell: info => {
                        // Access the quantity directly for rendering
                        const quantity = info.getValue()?.quantity ?? '';
                        return <EditableCell value={quantity} row={info.row} column={info.column}
                                             updateInventory={updateInventory}/>
                    }
                });
            });
        }

        return cols;
    }, [inventoryList, footerData]); // Add footerData as a dependency to ensure columns are recalculated when footer data changes

    if (isLoading) {
        return isLoadingSpinner();
    }

    return (
        <div style={parentStyle}>
            <StackedContentContainer title={"Equipment"}>
                <SearchableTable
                    columns={columns}
                    data={getFilteredInventory}
                    globalSearch={true}
                    actionParam={
                        <BRow>
                            <Col className={"my-auto mx-2"}>
                                <Form.Check
                                    label="Filter Zeros"
                                    checked={filterZeros}
                                    type="checkbox"
                                    onChange={toggleZeroFilter}
                                />
                            </Col>
                            <Col>
                                <Button className={'mx-2'} onClick={() => navigate('/resources/')}>
                                    View resources
                                </Button>
                            </Col>
                        </BRow>
                    }
                    customFooter={
                        footerData && (
                            <tfoot>
                            <tr>
                                <td><strong>Resource Totals</strong></td>
                                {columns
                                    .filter(col => col.id !== 'address')
                                    .map(col => {
                                        const resourceKey = col.id as string;
                                        // Use optional chaining and nullish coalescing to safely access properties
                                        const stock = footerData.stockValues?.[resourceKey] ?? 0;
                                        const displayName = footerData.displayNames?.[resourceKey] ?? resourceKey;
                                        return (
                                            <td key={`stock-${resourceKey}`} title={displayName}>
                                                <strong>{stock}</strong>
                                            </td>
                                        );
                                    })}
                            </tr>
                            <tr>
                                <td><strong>Inventory Differences</strong></td>
                                {columns
                                    .filter(col => col.id !== 'address')
                                    .map(col => {
                                        const resourceKey = col.id as string;
                                        // Use optional chaining and nullish coalescing to safely access properties
                                        const diff = footerData.diff?.[resourceKey] ?? 0;
                                        const color = diff < 0 ? "text-danger" : (diff === 0 ? "text-success" : "text-primary");
                                        const displayName = footerData.displayNames?.[resourceKey] ?? resourceKey;
                                        return (
                                            <td key={`diff-${resourceKey}`} title={displayName}>
                                                <p className={color}>{diff}</p>
                                            </td>
                                        );
                                    })}
                            </tr>
                            </tfoot>
                        )
                    }
                    hideFilters={true}
                />
            </StackedContentContainer>
        </div>
    );
};

export default ManageEquipmentPage;
